import Alert from '@/components/Alert';
import Button from '@/components/Button';
import { TextField } from '@/components/Forms/fields';
import { useSessionManager } from '@/lib/SessionTracking/useSession';
import {
  getStudentQuickSignInErrorMessage,
  STUDENT_QUICK_SIGN_IN_ERROR_CODES,
} from '@/utility/studentQuickSignIn';
import { signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import { FormEvent, useMemo, useState } from 'react';

const buildCallbackUrl = (roomCode: string) =>
  `/reports/room-code?roomCode=${encodeURIComponent(roomCode)}`;

const StudentRoomLoginForm = () => {
  const router = useRouter();
  const sessionManager = useSessionManager();
  const [roomCode, setRoomCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [requiresLastName, setRequiresLastName] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const shouldShowLastName = useMemo(
    () => requiresLastName || lastName.length > 0,
    [lastName.length, requiresLastName]
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');

    const normalizedRoomCode = roomCode.trim().toUpperCase();
    const normalizedFirstName = firstName.trim();
    const normalizedLastName = lastName.trim();

    if (!normalizedRoomCode || !normalizedFirstName) {
      setErrorMessage('Room code and first name are required.');
      return;
    }

    if (requiresLastName && !normalizedLastName) {
      setErrorMessage('Please enter your last name so we can find you.');
      return;
    }

    setIsSubmitting(true);
    const callbackUrl = buildCallbackUrl(normalizedRoomCode);
    const sessionId = sessionManager?.getSessionId();

    try {
      const response = await signIn('student-room-login', {
        roomCode: normalizedRoomCode,
        firstName: normalizedFirstName,
        lastName: normalizedLastName || undefined,
        sessionId: sessionId || undefined,
        callbackUrl,
        redirect: false,
      });

      if (response?.ok) {
        await router.push(response.url ?? callbackUrl);
        return;
      }

      if (
        response?.error ===
          STUDENT_QUICK_SIGN_IN_ERROR_CODES.lastNameRequired ||
        response?.error === STUDENT_QUICK_SIGN_IN_ERROR_CODES.lastNameMismatch
      ) {
        setRequiresLastName(true);
      }
      const resolvedError =
        response?.error ===
          STUDENT_QUICK_SIGN_IN_ERROR_CODES.lastNameMismatch &&
        !normalizedLastName
          ? getStudentQuickSignInErrorMessage(
              STUDENT_QUICK_SIGN_IN_ERROR_CODES.lastNameRequired
            )
          : getStudentQuickSignInErrorMessage(response?.error);
      setErrorMessage(resolvedError);
    } catch (error) {
      setErrorMessage('Unable to sign in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full p-5 sm:max-w-md justify-center align-middle shadow rounded bg-white text-black">
      <div className="flex flex-col justify-between gap-4 mt-5">
        {errorMessage && (
          <div className="flex">
            <Alert variant="error" message={errorMessage} />
          </div>
        )}
        <div className="text-center font-semibold">
          Room Code Log-in (Student Only)
        </div>
        <p className="text-xs text-gray-600 text-center">
          Your educator must enable Room Code Log-in. Enter the room code and
          your first name to sign in. We&apos;ll ask for your last name if
          another student shares your first name.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            label="Room Code"
            name="roomCode"
            placeholder="ABC123"
            value={roomCode}
            onChange={(event) => {
              setRoomCode(event.target.value);
              setErrorMessage('');
              setRequiresLastName(false);
            }}
            disabled={isSubmitting}
            autoComplete="off"
          />
          <TextField
            label="First Name"
            name="firstName"
            placeholder="Enter your first name"
            value={firstName}
            onChange={(event) => {
              setFirstName(event.target.value);
              setErrorMessage('');
              setRequiresLastName(false);
            }}
            disabled={isSubmitting}
            autoComplete="given-name"
          />
          {shouldShowLastName && (
            <TextField
              label="Last Name"
              name="lastName"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(event) => {
                setLastName(event.target.value);
                setErrorMessage('');
              }}
              disabled={isSubmitting}
              autoComplete="family-name"
            />
          )}
          <Button
            type="submit"
            className="w-full"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Continue
          </Button>
        </form>
      </div>
    </div>
  );
};

export default StudentRoomLoginForm;
